import React, { useContext, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import ThemeContext from '../context/ThemeContext';
import WidgetNameContext from '../context/WidgetNameContext';
import { applyPlotTheme } from '../utils/plotTheme';
import PlotExportDialog from './PlotExportDialog';

// One adapter lets every chart respond to theme changes, including exported plots.
export const desktopPlotData = (data, desktop = process.env.REACT_APP_DESKTOP === 'true') => (
  desktop && Array.isArray(data)
    ? data.flatMap((trace) => {
        // Many simultaneous WebGL charts can exhaust Chromium's graphics
        // contexts. Desktop charts use the bundled SVG/canvas renderers.
        if (trace.type === 'scattergl') return { ...trace, type: 'scatter' };
        if (trace.type === 'heatmapgl') return { ...trace, type: 'heatmap' };
        if (trace.type === 'splom') {
          const { dimensions = [], diagonal, ...scatter } = trace;
          return dimensions.flatMap((y, row) => dimensions.flatMap((x, column) => {
            if (row === column) return [];
            const axis = row * dimensions.length + column + 1;
            return [{ ...scatter, type: 'scatter', mode: 'markers',
              x: x.values, y: y.values, xaxis: `x${axis}`, yaxis: `y${axis}`,
              showlegend: trace.showlegend !== false && row === 0 && column === 1,
            }];
          }));
        }
        return trace;
      })
    : data
);

export const desktopPlotLayout = (data, layout, desktop = process.env.REACT_APP_DESKTOP === 'true') => {
  const dimensions = desktop && data?.find((trace) => trace.type === 'splom')?.dimensions;
  if (!dimensions?.length) return layout;
  const next = { ...layout, grid: { rows: dimensions.length, columns: dimensions.length, pattern: 'independent' } };
  dimensions.forEach((y, row) => dimensions.forEach((x, column) => {
    const index = row * dimensions.length + column + 1;
    const suffix = index === 1 ? '' : index;
    next[`xaxis${suffix}`] = { title: { text: x.label }, visible: row !== column, zeroline: false };
    next[`yaxis${suffix}`] = { title: { text: y.label }, visible: row !== column, zeroline: false };
  }));
  return next;
};

export const imageFilename = (name) => (
  (typeof name === 'string' ? name : '')
    .replace(/<[^>]*>/g, '').replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
    .trim().replace(/[. ]+$/g, '') || 'SpikeScope'
);

const ThemedPlot = React.forwardRef(({ layout, data, config, ...props }, ref) => {
  const widgetName = useContext(WidgetNameContext);
  const [exportGraph, setExportGraph] = useState(null);
  const context = useContext(ThemeContext);
  const theme = context?.theme || 'dark';
  const themedLayout = useMemo(() => applyPlotTheme(desktopPlotLayout(data, layout), theme), [data, layout, theme]);
  const plotData = useMemo(() => desktopPlotData(data), [data]);
  const exportConfig = {
    ...config,
    modeBarButtonsToRemove: [...(config?.modeBarButtonsToRemove || []), 'toImage'],
    modeBarButtonsToAdd: [...(config?.modeBarButtonsToAdd || []), {
      name: 'Download image',
      icon: { width: 24, height: 24, path: 'M3 5h5l2-2h4l2 2h5v16H3z M8 13a4 4 0 1 0 8 0a4 4 0 1 0-8 0' },
      click: graph => setExportGraph(graph),
    }],
    toImageButtonOptions: {
      filename: imageFilename(widgetName || layout?.title?.text || layout?.title),
      ...config?.toImageButtonOptions,
    },
  };
  return <><Plot {...props} config={exportConfig} data={plotData} ref={ref} layout={themedLayout} />
    {exportGraph && <PlotExportDialog graph={exportGraph} options={exportConfig.toImageButtonOptions}
      onClose={() => setExportGraph(null)} />}</>;
});

export default ThemedPlot;
